import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
  useContentToken,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons';

const PORTRAIT =
  '';

// Legacy headshots are circle-cropped on a white square; zooming hides the white corners.
const CIRCLE_HEADSHOT = /^https:\/\/irp\.cdn-website\.com\/[^/]+\/dms3rep\/multi\/[^/]+\.png$/i;

const backDef: SlotGroupDefinition = {
  label: 'Back Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/team' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Back to team' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const profileDef: SlotGroupDefinition = {
  label: 'Profile',
  fields: {
    image: {
      label: 'Photo',
      type: 'image',
      defaultValue:
        '',
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Team member' },
    email: {
      label: 'Email',
      type: 'email',
      defaultValue: 'firstname.lastname@alapartments.com',
    },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    linkedin_label: {
      label: 'LinkedIn Label',
      type: 'text',
      defaultValue: 'View LinkedIn Bio',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

interface TeamMemberBioProps {
  tokenPrefix?: string;
  name?: string;
  role?: string;
  company?: string;
  bio?: string;
  image?: string;
  alt?: string;
  email?: string;
  linkedin?: string;
  linkedinLabel?: string;
}

export function TeamMemberBio({
  tokenPrefix,
  name: nameProp,
  role: roleProp,
  company: companyProp,
  bio: bioProp,
  image: imageProp,
  alt: altProp,
  email: emailProp,
  linkedin: linkedinProp,
  linkedinLabel: linkedinLabelProp,
}: TeamMemberBioProps) {
  const prefix = tokenPrefix || 'teammemberbio';
  const namePath = `${prefix}.name`;
  const rolePath = `${prefix}.role`;
  const companyPath = `${prefix}.company`;
  const bioPath = `${prefix}.bio`;
  const backPath = `${prefix}.back`;
  const profilePath = `${prefix}.profile`;

  const name = useContentToken(namePath, {
    defaultValue: nameProp || 'Team Member',
  });
  const role = useContentToken(rolePath, {
    defaultValue: roleProp || 'Leadership',
  });
  const company = useContentToken(companyPath, {
    defaultValue: companyProp || 'American Landmark',
  });

  const runtimeProfileDef = useMemo<SlotGroupDefinition>(
    () => ({
      label: profileDef.label,
      fields: {
        image: {
          ...profileDef.fields.image,
          defaultValue: imageProp || profileDef.fields.image.defaultValue,
        },
        alt: {
          ...profileDef.fields.alt,
          defaultValue: altProp || nameProp || profileDef.fields.alt.defaultValue,
        },
        email: {
          ...profileDef.fields.email,
          defaultValue: emailProp || profileDef.fields.email.defaultValue,
        },
        linkedin: {
          ...profileDef.fields.linkedin,
          defaultValue: linkedinProp || profileDef.fields.linkedin.defaultValue,
        },
        linkedin_label: {
          ...profileDef.fields.linkedin_label,
          defaultValue: linkedinLabelProp || profileDef.fields.linkedin_label.defaultValue,
        },
        _target: profileDef.fields._target,
      },
    }),
    [altProp, emailProp, imageProp, linkedinLabelProp, linkedinProp, nameProp],
  );

  const back = useSlotGroup(backPath, backDef);
  const profile = useSlotGroup(profilePath, runtimeProfileDef);

  const titleLine = [role || roleProp, company || companyProp].filter(Boolean).join(' | ');
  const photo = profile.image || imageProp || PORTRAIT;
  const photoAlt = profile.alt || altProp || name || nameProp || '';
  const email = profile.email || emailProp || '';
  const linkedin = profile.linkedin || linkedinProp || '';
  const linkedinLabel = profile.linkedin_label || linkedinLabelProp || 'View LinkedIn Bio';
  const linkedinTarget = profile._target || '_blank';

  return (
    <section className="w-full overflow-x-clip bg-[#F5F7F9] py-10 md:py-16 lg:py-20">
      <div className="mx-auto w-full max-w-[1100px] px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <EditableSlotGroup groupPath={backPath} definition={backDef}>
            <a
              href={back.url || '/team'}
              target={back._target || '_self'}
              className="mb-6 inline-flex items-center gap-2 font-heading text-sm font-medium text-[#0A1626] transition-colors hover:text-[rgb(var(--ala-secondary))] md:mb-8"
            >
              <ArrowLeft size={16} aria-hidden />
              {back.label || 'Back to team'}
            </a>
          </EditableSlotGroup>
        </motion.div>

        <div className="grid items-start gap-5 md:grid-cols-[minmax(260px,36%)_1fr] md:gap-6 lg:gap-8">
          <EditableSlotGroup groupPath={profilePath} definition={runtimeProfileDef}>
            <div className="flex flex-col gap-4 md:gap-5">
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[3/4] w-full overflow-hidden rounded-2xl bg-[#0A1626] shadow-[0_8px_40px_rgba(10,22,38,0.08)]"
              >
                <VibesitesImage
                  url={photo}
                  alt={photoAlt}
                  sizes="(max-width: 768px) 100vw, 420px"
                  className={`absolute inset-0 h-full w-full object-cover object-center ${
                    CIRCLE_HEADSHOT.test(photo) ? 'scale-[1.42]' : ''
                  }`}
                />
              </motion.div>

              {email || linkedin ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
                  className="flex flex-col gap-3 rounded-2xl bg-[rgb(var(--ala-background))] p-4 shadow-[0_8px_40px_rgba(10,22,38,0.08)] md:p-5"
                >
                  {email ? (
                    <a
                      href={`mailto:${email}`}
                      className="inline-flex min-w-0 items-center justify-center gap-3 rounded-full border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 py-3 font-heading text-sm text-[#0A1626] transition-colors hover:border-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-secondary))] md:justify-start"
                    >
                      <Mail size={16} className="shrink-0 text-[rgb(var(--ala-text-muted))]" aria-hidden />
                      <span className="truncate">{email}</span>
                    </a>
                  ) : null}
                  {linkedin ? (
                    <a
                      href={linkedin}
                      target={linkedinTarget}
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-3 rounded-full border border-[rgb(var(--ala-border))] bg-[rgb(var(--ala-background))] px-4 py-3 font-heading text-sm text-[#0A1626] transition-colors hover:border-[rgb(var(--ala-secondary))] hover:text-[rgb(var(--ala-secondary))] md:justify-start"
                    >
                      <FontAwesomeIcon
                        icon={faLinkedinIn}
                        className="h-4 w-4 shrink-0 text-[rgb(var(--ala-text-muted))]"
                        aria-hidden
                      />
                      <span>{linkedinLabel}</span>
                    </a>
                  ) : null}
                </motion.div>
              ) : null}
            </div>
          </EditableSlotGroup>

          <motion.article
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl bg-[rgb(var(--ala-background))] px-6 py-8 text-center shadow-[0_8px_40px_rgba(10,22,38,0.08)] md:px-10 md:py-12 md:text-left lg:px-14 lg:py-16"
          >
            <EditableToken path={namePath} as="h1" label="Name" defaultValue={nameProp || 'Team Member'}>
              <span className="font-heading text-[clamp(2rem,4vw,3.25rem)] font-semibold leading-[1.1] tracking-[-0.03em] text-[#0A1626]">
                {name || nameProp || 'Team Member'}
              </span>
            </EditableToken>

            <p className="mt-3 font-heading text-base text-[#6B7C8E] md:text-lg">
              <EditableToken path={rolePath} as="span" label="Role" defaultValue={roleProp || 'Leadership'}>
                {role || roleProp || 'Leadership'}
              </EditableToken>
              <span aria-hidden> | </span>
              <EditableToken
                path={companyPath}
                as="span"
                label="Company"
                defaultValue={companyProp || 'American Landmark'}
              >
                {company || companyProp || 'American Landmark'}
              </EditableToken>
            </p>
            <span className="sr-only">{titleLine}</span>

            <div className="my-6 h-px w-full bg-[rgb(var(--ala-border))] md:my-8" />

            <RichTextContent
              path={bioPath}
              label="Biography"
              as="div"
              defaultValue={
                bioProp ||
                '<p>This team member helps lead American Landmark with a focus on operational excellence and long-term value across the portfolio.</p>'
              }
              className="max-w-[34rem] text-left font-body text-base leading-[1.75] text-[#4B5563] [&_p]:mb-4 [&_p:last-child]:mb-0"
            />
          </motion.article>
        </div>
      </div>

      {/* Schema-lint anchors */}
      <div className="hidden" style={{ display: 'none' }}>
        <EditableToken path="teammemberbio.name" as="span">
          {''}
        </EditableToken>
        <EditableToken path="teammemberbio.role" as="span">
          {''}
        </EditableToken>
        <EditableToken path="teammemberbio.company" as="span">
          {''}
        </EditableToken>
        <RichTextContent path="teammemberbio.bio" as="div" />
        <EditableSlotGroup groupPath="teammemberbio.back" definition={backDef}>
          <span />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="teammemberbio.profile" definition={profileDef}>
          <span />
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'TeamMemberBio',
  displayName: 'Team Member Bio',
  tokens: {
    'teammemberbio.name': {
      label: 'Name',
      type: 'text',
      defaultValue: 'Team Member',
    },
    'teammemberbio.role': {
      label: 'Role',
      type: 'text',
      defaultValue: 'Leadership',
    },
    'teammemberbio.company': {
      label: 'Company',
      type: 'text',
      defaultValue: 'American Landmark',
    },
    'teammemberbio.bio': {
      label: 'Biography',
      type: 'richtext',
      defaultValue:
        '<p>This team member helps lead American Landmark with a focus on operational excellence and long-term value across the portfolio.</p>',
    },
  },
  slotGroups: {
    'teammemberbio.back': backDef,
    'teammemberbio.profile': profileDef,
  },
};

(TeamMemberBio as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TeamMemberBio;
